import type { AssessmentRespondentType, AssessmentSendableDefinition } from '@cartwheel/api/v1'
import {
  clientAssessmentResultsQueryKey,
  clientAssessmentSendOptionsQueryKey,
  useClientAssessmentSendOptionsQuery,
  useCreateAssessmentManualSendMutation,
  useToggleAssessmentAutoSendMutation,
} from '@cartwheel/api/v1'
import { Button, Checkbox, ErrorView, LoadingView, Switch, useToast } from '@cartwheel/components'
import { useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'

interface Props {
  clientId: string
  instrumentKey: string
}

const RESPONDENT_TYPES = ['student', 'guardian', 'clinician'] as const

const RESPONDENT_LABELS: Record<AssessmentRespondentType, string> = {
  student: 'Student',
  guardian: 'Parent/guardian',
  clinician: 'Clinician',
}

function enabledRespondents(definition: AssessmentSendableDefinition): AssessmentRespondentType[] {
  return RESPONDENT_TYPES.filter((type) => definition.respondent_types[type])
}

export default function ClientAssessmentSendActions({ clientId, instrumentKey }: Props) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const autoSendSwitchId = useId()
  const { data, isLoading, error } = useClientAssessmentSendOptionsQuery({
    path: { client_id: clientId },
  })

  const [selectedRespondents, setSelectedRespondents] = useState<AssessmentRespondentType[]>([])

  const sendMutation = useCreateAssessmentManualSendMutation()
  const toggleMutation = useToggleAssessmentAutoSendMutation()

  if (isLoading) return <LoadingView title="Loading assessment options…" className="py-8" />
  if (error)
    return (
      <ErrorView title="Error loading assessment options" message="Please refresh to try again." />
    )

  const definition = (data?.definitions ?? []).find(
    (candidate) => candidate.instrument_key === instrumentKey
  )
  if (!definition) return null

  const canManageAutoSend = data?.can_manage_auto_send ?? false
  const respondents = enabledRespondents(definition)
  const chosenRespondents = selectedRespondents.filter((type) => respondents.includes(type))

  const toggleRespondent = (type: AssessmentRespondentType) => {
    setSelectedRespondents((current) =>
      current.includes(type) ? current.filter((value) => value !== type) : [...current, type]
    )
  }

  const send = (mode: 'send' | 'administer_now') => {
    sendMutation.mutate(
      {
        body: {
          definition_id: definition.definition_id,
          client_id: clientId,
          respondent_types: chosenRespondents,
          mode,
        },
      },
      {
        onSuccess: (result) => {
          if (mode === 'administer_now') {
            if (result.staff_url) window.open(result.staff_url, '_blank', 'noopener')
            toast.success({ description: `${definition.name} ready to administer.` })
          } else {
            toast.success({ description: `${definition.name} sent.` })
          }
          setSelectedRespondents([])
          queryClient.invalidateQueries({
            queryKey: clientAssessmentResultsQueryKey({ path: { client_id: clientId } }),
          })
        },
        onError: () =>
          toast.error({ description: 'Could not complete that action. Please try again.' }),
      }
    )
  }

  const toggleAutoSend = (enabled: boolean) => {
    toggleMutation.mutate(
      {
        path: { client_id: clientId },
        body: { definition_id: definition.definition_id, disabled: !enabled },
      },
      {
        onSuccess: () =>
          queryClient.invalidateQueries({
            queryKey: clientAssessmentSendOptionsQueryKey({ path: { client_id: clientId } }),
          }),
        onError: () =>
          toast.error({ description: 'Could not update auto-send. Please try again.' }),
      }
    )
  }

  const canSend = chosenRespondents.length > 0 && !sendMutation.isPending
  const canAdminister = chosenRespondents.length === 1 && !sendMutation.isPending

  return (
    <div className="border-b border-gray-stroke px-4 py-6 lg:px-8">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h3 className="mb-4 text-lg font-medium text-charcoal">Send {definition.name}</h3>

          <span className="mb-1 block text-sm font-medium text-charcoal-75">Respondents</span>
          <div className="flex flex-wrap gap-4">
            {respondents.map((type) => (
              <div key={type} className="flex items-center gap-2 text-sm text-charcoal">
                <Checkbox
                  id={`respondent-${type}`}
                  checked={chosenRespondents.includes(type)}
                  onChange={() => toggleRespondent(type)}
                  value={type}
                />
                <label htmlFor={`respondent-${type}`}>{RESPONDENT_LABELS[type]}</label>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <Button variant="primary" disabled={!canSend} onClick={() => send('send')}>
              Send assessment
            </Button>
            <Button
              variant="secondary"
              disabled={!canAdminister}
              onClick={() => send('administer_now')}
            >
              Administer now
            </Button>
          </div>
          {chosenRespondents.length > 1 && (
            <p className="mt-2 text-xs text-charcoal-50">
              Administer now supports a single respondent.
            </p>
          )}
        </div>

        {canManageAutoSend && (
          <div className="flex items-center justify-between gap-4 rounded-lg bg-white-dark p-4 lg:w-80">
            <div>
              <label htmlFor={autoSendSwitchId} className="font-medium text-charcoal">
                Automated sends
              </label>
              <div className="text-sm text-charcoal-50">
                Turn off to pause automated sends of this assessment for this student.
              </div>
            </div>
            <Switch
              id={autoSendSwitchId}
              checked={!definition.auto_send_disabled}
              onChange={toggleAutoSend}
              disabled={toggleMutation.isPending}
            />
          </div>
        )}
      </div>
    </div>
  )
}
